import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import format from 'string-template';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { observer } from 'mobx-react';
import { isAIMealPlanButtonEnabled } from '../../../../../utils/postHog';
import { ReactComponent as RefreshIcon } from '../../../../../assets/icons/refresh-icon.svg';
import { ReactComponent as LightRefreshIcon } from '../../../../../assets/icons/refresh-icon-2.svg';
import {
  AttentionTag,
  InfoTag,
  DisabledTag,
  WarningTag,
} from '../../../../../components/Tags';
import Select from '../../../../../components/Select';
import useRunOnInitialRender from '../../../../../hooks/useRunOnInitialRender';
import { DateFormat } from '../../../../../utils/date';
import MealPlanAssignmentContext from '../../../../context/MealPlanAssignmentContext';
import MealPlanContext from '../../../../context/MealPlanContext';
import {
  AddButton,
  EditButton,
  PrimaryButton,
  SaveButton,
  CancelButton,
} from '../../../../../components/Button/ActionButtons';
import { DietaryRestrictionText } from '../../../../Model/UserNutritionProfile';
import MealPlan, { MealPlanType } from '../../../../Model/MealPlan';
import Recipe from '../../../../Model/Recipe';
import {
  getValidMealPlanOptions,
  addRecipeToMealTimes,
  removeRecipeFromMealTimes,
  moveRecipeInMealPlan,
} from '../../../../utils/mealPlan';
import {
  RestrictionAllergens,
  AllergenTagText,
  getMacroAverages,
  getMacroNutrientsFromCalories,
} from '../../../../utils/meals';
import MealPlanView from '../../../../components/MealPlanView';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';
import useMealPlanGeneration from '../../../../hooks/useMealPlanGeneration';
import useAIMealPlanGeneration from '../../../../hooks/useAIMealPlanGeneration';
import EditMealTimesModal from '../../ManageMealPlans/components/EditMealTimesModal';
import useToast from '../../../../hooks/useToast';
import { NutritionalGoalsAction } from '../UserNutritionalGoals';
import AddRecipeModal from '../AddRecipeModal';
import MealPlanSelect from '../MealPlanSelect/MealPlanSelect';

import AIMealPlanModal from '../AIMealPlanModal';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import UserContext from '../../../../../context/UserContext';
import GenerationStatus from '../GenerationStatus';
import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import {
  Container,
  TitleContainer,
  ContentContainer,
  ItemContainer,
  Label,
  StyledTextField,
  ItemList,
  StyledDateContainer,
  LoadingIndicatorContainer,
  StyledText,
  StyledDialogContent,
} from './styles';
import texts from './texts';
import { MealPlanAssignOption } from './utils';
import { FEEDBACK_FORM } from './constants';

// Shared, so a client with no plan for the selected product does not produce a fresh `[]`
// each render — the effects below depend on it, and a new identity every render loops.
const NO_MEAL_TIMES = [];

const UserMealPlan = ({
  isEditable,
  totalDailyCalories,
  scaleMeals,
  userId,
  macrosPercentages,
  defaultAction,
  onIsAIGeneratedChange,
  feedbackHandler,
  setFeedbackHandler,
}) => {
  const {
    mealPlanAssignmentDoc = {},
    userDoc = {},
    nutritionProfileDoc = {},
    nutritionalGoals = {},
    setNutritionalGoals,
    setNewMealPlanData,
    mealPlanAction,
    setMealPlanAction,
    nutritionalGoalsAction,
    assignmentNote,
    setAssignmentNote,
  } = useContext(MealPlanAssignmentContext);
  const { userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const { userId: actingCoachId } = useContext(UserContext);
  const {
    comnbinedMealPlanViews: mealPlanViews = [],
  } = useContext(MealPlanContext);

  const {
    dietaryRestrictions: restrictions = [],
    excludedIngredients = [],
  } = nutritionProfileDoc || {};
  const {
    name: assignedMealPlanName = '',
    mealPlanId: assignedMealPlanId,
    approvalDate: assignmentApprovalDate,
    mealTimes: assignmentMealTimes = NO_MEAL_TIMES,
    type: assignedMealPlanType = '',
  } = mealPlanAssignmentDoc || {};

  // Get valid, non archived, meal plans
  const validMealPlans = useMemo(() => (
    getValidMealPlanOptions(mealPlanViews)
  ), [
    mealPlanViews,
  ]);

  const { generateMealTimes } = useMealPlanGeneration();
  const { showToast } = useToast();

  // States used when assigning a new meal plan
  const [selectedFilter, setSelectedFilter] = useState('');
  const [selectedMealPlanId, setSelectedMealPlanId] = useState('');
  const [selectedBucketId, setSelectedBucketId] = useState('');
  const [selectedMealPlanTemplateDoc, setSelectedMealPlanTemplateDoc] = useState(null);
  const [filteredMealPlans, setFilteredMealPlans] = useState(validMealPlans);
  // Meal times to show in meal plan view component
  const [newMealTimes, setNewMealTimes] = useState(!isEditable ? assignmentMealTimes : []);
  const [newMealPlanName, setNewMealPlanName] = useState(!isEditable ? assignedMealPlanName : '');
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [isMealTimesModalOpen, setIsMealTimesModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [aiGeneratedRecipes, setAiGeneratedRecipes] = useState([]);
  const [isRecipeNameModalOpen, setIsRecipeNameModalOpen] = useState(false);
  const [recipeNameToUpdate, setRecipeNameToUpdate] = useState('');
  const [recipeIdToUpdate, setRecipeIdToUpdate] = useState(null);
  const [bucketIndexToUpdate, setBucketIndexToUpdate] = useState(null);
  const [isLoadingOverlayOpen, setIsLoadingOverlayOpen] = useState(false);

  const {
    aiMealPlannerRequestInFlight,
    aiMealPlanModalOpen,
    aiMealPlannerRequestId,
    handleCloseAIMealPlanModal,
    openAIMealPlanModal,
    handleGenerationComplete,
    setAIMealPlanModalOpen,
  } = useAIMealPlanGeneration({
    isTemplate: false,
    userId,
    setMealTimes: setNewMealTimes,
    setAiGeneratedRecipes,
    onIsAIGeneratedChange,
    errorMessage: texts.aiMealPlanGenerationError,
  });

  const assignOptions = useMemo(() => {
    let options = Object.values(MealPlanAssignOption);

    // Remove the AI meal planner option from the dashbaord if it is not enabled.
    if (!isAIMealPlanButtonEnabled()) {
      options = options.filter((option) => option !== MealPlanAssignOption.AI_MEAL_PLAN);
    }

    // Remove previous meal plan option if no meal plan is assigned
    if (!mealPlanAssignmentDoc?.exists) {
      options = options.filter((option) => option !== MealPlanAssignOption.USE_PREVIOUS_MEAL_PLAN);
    }

    // Generate the options for the enabled buttons
    return options.map((option) => ({
      label: texts.button[option],
      value: option,
    }));
  }, [
    mealPlanAssignmentDoc,
  ]);

  const getFilteredMealPlans = useCallback((filter = null) => {
    const resultMealPlans = validMealPlans || [];

    if (!filter) {
      return resultMealPlans;
    }

    // Filter by restrictions, in this case we should also filter live plans
    return resultMealPlans.filter((mealPlan) => {
      const { allergenTags, type } = mealPlan;
      return (
        type !== MealPlanType.LIVE
          && !allergenTags.some((tag) => RestrictionAllergens[filter].includes(tag))
      );
    });
  }, [
    validMealPlans,
  ]);

  const assignedMealPlan = useMemo(() => (
    mealPlanViews.find((mealPlan) => mealPlan.id === assignedMealPlanId)
  ), [
    mealPlanViews,
    assignedMealPlanId,
  ]);

  const selectMealPlan = useMemo(() => (
    mealPlanViews.find((mealPlan) => mealPlan.id === selectedMealPlanId)
  ), [
    mealPlanViews,
    selectedMealPlanId,
  ]);

  const openRecipeModal = (bucketId = null) => {
    setSelectedBucketId(bucketId);
    setIsRecipeModalOpen(true);
  };

  const closeRecipeModal = () => {
    setSelectedBucketId('');
    setIsRecipeModalOpen(false);
  };

  // Update filtered meal plans when meal plan views or filter changes
  useEffect(() => {
    setFilteredMealPlans(getFilteredMealPlans(selectedFilter));
  }, [
    getFilteredMealPlans,
    selectedFilter,
  ]);

  const filterOptions = {
    '': texts.allPlans,
    ...DietaryRestrictionText,
  };

  const filterSelectOptions = Object.entries(filterOptions).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const optionHandler = useMemo(() => ({
    [MealPlanAssignOption.NEW_MEAL_PLAN]: () => {
      if (selectedMealPlanId) {
        // Check if selected meal plan is in the filtered options
        if (filteredMealPlans.some((filteredMealPlan) => filteredMealPlan.id === selectedMealPlanId)) {
          // Don't update states, keep current meal plan selected
          setMealPlanAction(MealPlanAssignOption.NEW_MEAL_PLAN);
          return;
        }
      }
      // Reset states
      setSelectedMealPlanId('');
      setSelectedMealPlanTemplateDoc(null);
      setNewMealPlanData({});
      setNewMealTimes([]);
      setNewMealPlanName('');
      setMealPlanAction(MealPlanAssignOption.NEW_MEAL_PLAN);
      onIsAIGeneratedChange(false);
    },
    [MealPlanAssignOption.USE_PREVIOUS_MEAL_PLAN]: () => {
      // Set states to assigned meal plan values
      setSelectedMealPlanId(assignedMealPlanId);
      setSelectedMealPlanTemplateDoc(null);
      setNewMealTimes(assignmentMealTimes);
      setNewMealPlanName(assignedMealPlanName);
      setMealPlanAction(MealPlanAssignOption.USE_PREVIOUS_MEAL_PLAN);
      setSelectedFilter('');
      onIsAIGeneratedChange(false);
    },
    [MealPlanAssignOption.AI_MEAL_PLAN]: () => {
      setMealPlanAction(MealPlanAssignOption.AI_MEAL_PLAN);
      setSelectedMealPlanId('');
      setNewMealPlanData({});
      setNewMealTimes([]);
      setNewMealPlanName('');
      onIsAIGeneratedChange(false);
    },
  }), [
    assignedMealPlanId,
    assignmentMealTimes,
    assignedMealPlanName,
    setNewMealPlanData,
    setMealPlanAction,
    selectedMealPlanId,
    filteredMealPlans,
    onIsAIGeneratedChange,
  ]);

  // The meal plan re-generation option should be shown initially if the user has a live meal plan assigned
  const showInitialRegeneration = useMemo(() => (
    isEditable && assignedMealPlan?.type === MealPlanType.LIVE && !selectedMealPlanTemplateDoc
  ), [
    selectedMealPlanTemplateDoc,
    assignedMealPlan,
    isEditable,
  ]);

  const newMacroAverages = useMemo(() => (
    getMacroAverages(newMealTimes || [])
  ), [newMealTimes]);

  const newMealTimesMetadata = useMemo(() => {
    // Get allergen tags and recipe ids
    const allAllergenTags = [];
    const recipes = [];
    newMealTimes?.forEach(({ meals }) => meals.forEach(({ recipe }) => {
      recipe.allergenTags?.forEach((tag) => allAllergenTags.push(tag));
      recipes.push(recipe.id);
    }));
    return {
      allergenTags: [...new Set(allAllergenTags)],
      recipes: [...new Set(recipes)],
    };
  }, [newMealTimes]);

  useRunOnInitialRender(() => {
    if (defaultAction) {
      optionHandler[defaultAction]();
    }
  });

  useEffect(() => {
    if (isEditable) {
      return;
    }
    setNewMealPlanName(assignedMealPlanName);
    setNewMealTimes(assignmentMealTimes);
  }, [
    isEditable,
    assignedMealPlanName,
    assignmentMealTimes,
  ]);

  /*
    This useEffect handles meal plan changes, it sets the refresh flow's meal plan data (new macro averages, new
    meal times, new allergens, etc).
  */
  useEffect(() => {
    if (!mealPlanAction) {
      return;
    }

    // Set refresh flow meal plan data
    const commonFields = {
      mealTimes: newMealTimes,
      name: newMealPlanName,
      macroAverages: newMacroAverages,
      allergenTags: newMealTimesMetadata.allergenTags,
      recipes: newMealTimesMetadata.recipes,
    };

    if (mealPlanAction === MealPlanAssignOption.NEW_MEAL_PLAN && selectedMealPlanId) {
      setNewMealPlanData({
        ...commonFields,
        mealPlanId: selectedMealPlanId,
        type: selectMealPlan?.type,
      });
    } else if (mealPlanAction === MealPlanAssignOption.USE_PREVIOUS_MEAL_PLAN) {
      setNewMealPlanData({
        ...commonFields,
        mealPlanId: assignedMealPlanId,
        type: assignedMealPlanType,
      });
    } else if (mealPlanAction === MealPlanAssignOption.AI_MEAL_PLAN) {
      setNewMealPlanData({
        macroAverages: {
          percentages: { ...nutritionalGoals?.macroAverages?.percentages },
          macros: { ...nutritionalGoals?.macroAverages?.macros },
        },
        recipes: newMealTimesMetadata.recipes,
        allergenTags: newMealTimesMetadata.allergenTags,
        mealPlanId: '',
        mealTimes: newMealTimes,
        name: `AI Meal Plan for ${userDoc.firstName} ${userDoc.lastName} - ${new Date().toLocaleDateString()}`,
        mealPlanRequestId: aiMealPlannerRequestId,
        isGeneratedByAI: true,
        type: '',
      });
    }
  }, [
    mealPlanAction,
    setNewMealPlanData,
    newMealTimes,
    newMacroAverages,
    selectedMealPlanId,
    newMealPlanName,
    assignedMealPlanId,
    assignedMealPlanType,
    selectMealPlan,
    newMealTimesMetadata,
    aiMealPlannerRequestId,
    userDoc.firstName,
    userDoc.lastName,
    nutritionalGoals,
  ]);

  // Set recommended nutritional goals based on new meal plan content
  useEffect(() => {
    if (nutritionalGoalsAction === NutritionalGoalsAction.USE_RECOMMENDED_GOALS && newMacroAverages) {
      setNutritionalGoals((prevGoals) => ({
        ...prevGoals,
        macroAverages: {
          macros: getMacroNutrientsFromCalories(prevGoals?.totalDailyCalories, newMacroAverages.percentages),
          percentages: { ...newMacroAverages.percentages },
        },
      }));
    }
  }, [
    nutritionalGoalsAction,
    newMacroAverages,
    setNutritionalGoals,
  ]);

  const handleAddRecipe = (bucketId, recipe) => {
    setNewMealTimes((prev) => addRecipeToMealTimes(prev, bucketId, recipe));
  };

  const handleDeleteRecipe = (recipeId, bucketIndex) => {
    setNewMealTimes((prev) => removeRecipeFromMealTimes(prev, recipeId, bucketIndex));
  };

  const onClickSaveRecipe = (recipeId, bucketIndex) => {
    // Find the recipe in mealTimes
    const mealTime = newMealTimes[bucketIndex];
    if (!mealTime) return;

    const meal = mealTime.meals.find((m) => m.recipe.id === recipeId);
    if (!meal) return;

    // Set state to open modal with current recipe name
    setRecipeIdToUpdate(recipeId);
    setBucketIndexToUpdate(bucketIndex);
    setRecipeNameToUpdate(meal.recipe.name || '');
    setIsRecipeNameModalOpen(true);
  };

  const handleCloseRecipeNameModal = useCallback(() => {
    setIsRecipeNameModalOpen(false);
    setRecipeIdToUpdate(null);
    setBucketIndexToUpdate(null);
    setRecipeNameToUpdate('');
  }, []);

  const handleSaveRecipe = useCallback(async () => {
    setIsLoadingOverlayOpen(true);
    if (!recipeIdToUpdate || bucketIndexToUpdate === null || !recipeNameToUpdate.trim()) {
      setIsLoadingOverlayOpen(false);
      return;
    }

    try {
      const recipe = new Recipe(recipeIdToUpdate);
      await recipe.init();

      const coachId = actingCoachId;

      await recipe.updateFields({
        name: recipeNameToUpdate.trim(),
        createdBy: coachId,
        isAIGenerated: true,
        isArchived: false,
        updatedAt: new Date(),
        updatedBy: loggedInUserDoc?.id,
      });

      // Update the local state to reflect the recipe changes
      setNewMealTimes((prevMealTimes) => {
        const updatedMealTimes = [...prevMealTimes];
        const mealTime = updatedMealTimes[bucketIndexToUpdate];

        if (mealTime) {
          const updatedMeals = mealTime.meals.map((meal) => {
            if (meal.recipe.id === recipeIdToUpdate) {
              return {
                ...meal,
                recipe: {
                  ...meal.recipe,
                  createdBy: coachId,
                },
              };
            }
            return meal;
          });

          updatedMealTimes[bucketIndexToUpdate] = {
            ...mealTime,
            meals: updatedMeals,
          };
        }

        return updatedMealTimes;
      });

      showToast(texts.recipeSavedSuccessfully);

      handleCloseRecipeNameModal();
    } catch (error) {
      showToast(format(texts.recipeSaveFailed, { error: error.message }), { error: true });
    } finally {
      setIsLoadingOverlayOpen(false);
    }
  }, [
    recipeIdToUpdate,
    bucketIndexToUpdate,
    recipeNameToUpdate,
    actingCoachId,
    loggedInUserDoc,
    showToast,
    handleCloseRecipeNameModal,
  ]);

  const handleMoveRecipe = ({
    removedIndex,
    addedIndex,
    sourceBucketIndex,
    destinationBucketIndex,
    meal,
  }) => {
    setNewMealTimes((prevMealTimes) => moveRecipeInMealPlan(
      prevMealTimes,
      removedIndex,
      addedIndex,
      sourceBucketIndex,
      destinationBucketIndex,
      meal,
    ));
  };

  const handleFilterChange = useCallback((event) => {
    const filterValue = event.target.value;
    const newFilteredMealPlans = getFilteredMealPlans(filterValue);
    if (selectedMealPlanId) {
      // Check if selected meal plan is in the filtered options
      if (newFilteredMealPlans.some((filteredMealPlan) => filteredMealPlan.id === selectedMealPlanId)) {
        // Don't update states, keep current meal plan selected
        setSelectedFilter(filterValue);
        return;
      }
    }
    setSelectedFilter(filterValue);
    // Reset states
    setSelectedMealPlanId('');
    setNewMealPlanData({});
    setNewMealTimes([]);
    setNewMealPlanName('');
  }, [
    getFilteredMealPlans,
    setNewMealPlanData,
    selectedMealPlanId,
  ]);

  // Gets a live template as a parameter and returns a generated array of meal times
  const getGeneratedMealTimes = useCallback(async (mealPlanTemplate) => {
    const {
      macroAverages: {
        percentages: {
          protein,
          carbs,
          fat,
        } = {},
      } = {},
    } = nutritionalGoals;

    return generateMealTimes({
      mealPlanTemplate,
      restrictions,
      userId,
      excludedIngredientIds: excludedIngredients.map(({ id }) => id),
      ...((protein && carbs && fat) ? {
        targetProtein: protein,
        targetCarbs: carbs,
        targetFat: fat,
      } : {}),
    });
  }, [
    nutritionalGoals,
    restrictions,
    userId,
    excludedIngredients,
    generateMealTimes,
  ]);

  const handleMealPlanTemplateSelect = useCallback(async (event) => {
    const mealPlanId = event.target.value;
    setIsLoading(true);
    const mealPlanTemplateDoc = await MealPlan.getMealPlan(mealPlanId);
    const {
      type,
      name,
      mealTimes: templateMealTimes,
    } = mealPlanTemplateDoc;

    let processedMealTimes;

    if (type === MealPlanType.PREDEFINED) {
      // For predefined plans, use the template meal times
      processedMealTimes = templateMealTimes;
    } else {
      // For live meal plans, generate new meal times based on the template
      processedMealTimes = await getGeneratedMealTimes(mealPlanTemplateDoc);
    }

    // Process the meal times to ensure proper ID assignment
    const finalMealTimes = processedMealTimes?.map((newMealTime) => {
      // Try to find an existing meal time with the same name in the current assignment
      const existingMatchingMealTime = assignmentMealTimes.find(
        (assignmentMealTime) => assignmentMealTime.name === newMealTime.name,
      );
      // Return the meal time with an ID, prioritizing:
      // 1. Existing assignment ID if found
      // 2. Template meal time ID if available
      // 3. Generate new UUID as fallback
      return {
        ...newMealTime,
        id: existingMatchingMealTime?.id || newMealTime.id || uuidv4(),
      };
    });

    setNewMealTimes(finalMealTimes || []);
    setSelectedMealPlanTemplateDoc(mealPlanTemplateDoc);
    setSelectedMealPlanId(mealPlanId);
    setNewMealPlanName(name);
    setIsLoading(false);
  }, [
    getGeneratedMealTimes,
    assignmentMealTimes,
  ]);

  // Re-generates and sets new meal times
  const handleRegeneratePlan = useCallback(async () => {
    setIsLoading(true);
    let generatedMealTimes;
    /*
     * If the client had a live meal plan assigned, the regenerate option will be visible.
     * In that case we need to fetch the meal plan since a selected template is not available.
     */
    if (showInitialRegeneration) {
      const mealPlanTemplateDoc = await MealPlan.getMealPlan(assignedMealPlanId);
      generatedMealTimes = await getGeneratedMealTimes(mealPlanTemplateDoc);
    } else {
      generatedMealTimes = await getGeneratedMealTimes(selectedMealPlanTemplateDoc);
    }

    // Process the generated meal times to ensure proper ID assignment
    const finalMealTimes = generatedMealTimes?.map((newMealTimeObject, index) => {
      const existingMealTime = newMealTimes[index];
      return {
        ...newMealTimeObject,
        // Use existing ID if available, otherwise generate a new one
        id: existingMealTime?.id || uuidv4(),
      };
    });
    setNewMealTimes(finalMealTimes || []);
    setIsLoading(false);
  }, [
    showInitialRegeneration,
    assignedMealPlanId,
    getGeneratedMealTimes,
    selectedMealPlanTemplateDoc,
    newMealTimes,
  ]);

  const renderItemList = useCallback(({
    items,
    itemsTexts,
    component: ItemComponent,
    visibleAmount = 4,
  }) => {
    // Show first {visibleAmount} items as badges and remaining ones as a tooltip.
    const firstItems = items.slice(0, visibleAmount);
    const remainingItems = items.slice(visibleAmount, items.length);
    const tooltipText = remainingItems.map((it) => itemsTexts[it]).join(', ');
    return (
      <ItemList>
        {firstItems.map((item) => (
          <ItemComponent key={`list-item-${item}`}>
            {itemsTexts[item]}
          </ItemComponent>
        ))}
        {!!remainingItems.length && (
          <Tooltip
            title={tooltipText}
            placement="top"
            arrow
          >
            <ItemComponent>
              {format(texts.moreItems, { amount: remainingItems.length })}
            </ItemComponent>
          </Tooltip>
        )}
      </ItemList>
    );
  }, []);

  const renderDietaryComplianceTags = useCallback(() => {
    if (!restrictions.length) {
      return null;
    }
    return (
      <ItemContainer>
        <Label>{texts.dietaryCompliance}</Label>
        {renderItemList({
          items: restrictions,
          itemsTexts: DietaryRestrictionText,
          component: AttentionTag,
        })}
      </ItemContainer>
    );
  }, [restrictions, renderItemList]);

  const renderIngredientExclusionTags = useCallback(() => {
    if (!excludedIngredients.length) return null;
    const exclusionTexts = Object.fromEntries(excludedIngredients.map(({ id, name }) => [id, name]));
    return (
      <ItemContainer>
        <Label>{texts.ingredientExclusions}</Label>
        {renderItemList({
          items: excludedIngredients.map(({ id }) => id),
          itemsTexts: exclusionTexts,
          component: WarningTag,
          visibleAmount: 2,
        })}
      </ItemContainer>
    );
  }, [excludedIngredients, renderItemList]);

  const renderAllergenTags = useCallback(() => (
    <ItemContainer>
      <Label $isAllergens>{texts.allergens}</Label>
      {newMealTimesMetadata.allergenTags.length
        ? renderItemList({
          items: newMealTimesMetadata.allergenTags,
          itemsTexts: AllergenTagText,
          component: AttentionTag,
          visibleAmount: 2,
        })
        : (
          <ItemList>
            <DisabledTag>
              {texts.noAllergens}
            </DisabledTag>
          </ItemList>
        )}
    </ItemContainer>
  ), [newMealTimesMetadata.allergenTags, renderItemList]);

  // Only show the regenerate button when a live template is selected
  const showRegenerateButton = !isLoading
    && (showInitialRegeneration || selectedMealPlanTemplateDoc?.type === MealPlanType.LIVE);

  const handleAIMealPlanFeedback = useCallback(() => {
    setFeedbackHandler(false);
    const acName = loggedInUserDoc?.name || '';
    const clientName = userDoc?.name || '';
    const clientId = userId || '';

    const googleFormLink = `${FEEDBACK_FORM}
      &entry.541106245=${encodeURIComponent(acName)}
      &entry.1172184532=${encodeURIComponent(clientName)}
      &entry.1214953572=${encodeURIComponent(clientId)}
      &entry.1305089500=${encodeURIComponent(aiGeneratedRecipes.join(','))}`;

    window.open(googleFormLink, '_blank');
  }, [setFeedbackHandler, loggedInUserDoc.name, userDoc.name, userId, aiGeneratedRecipes]);

  useEffect(() => {
    if (feedbackHandler) {
      handleAIMealPlanFeedback();
    }
  }, [feedbackHandler, handleAIMealPlanFeedback]);

  if (aiMealPlannerRequestId) {
    return (
      <GenerationStatus
        requestId={aiMealPlannerRequestId}
        onMealPlanGenerationComplete={handleGenerationComplete}
      />
    );
  }

  return (
    <>
      <Container>
        <TitleContainer>{texts.mealPlan}</TitleContainer>
        {isEditable && (
        <ContentContainer>
          <RadioButtonGroup
            options={assignOptions}
            selectedOption={mealPlanAction}
            onOptionChange={(value) => optionHandler[value]?.()}
          />
        </ContentContainer>
        )}
        {(!!mealPlanAction || !isEditable) && (
        <>
          {mealPlanAction === MealPlanAssignOption.AI_MEAL_PLAN ? (
            <ContentContainer>
              <ItemContainer>
                <PrimaryButton
                  onClick={openAIMealPlanModal}
                  size="medium"
                  disabled={aiMealPlannerRequestInFlight}
                >
                  {aiMealPlannerRequestInFlight ? texts.generatingAIMealPlan : texts.generateAIMealPlan}
                </PrimaryButton>
              </ItemContainer>
              <ItemContainer>
                <Label>{texts.generateAIMealPlanDescription}</Label>
              </ItemContainer>
              {renderDietaryComplianceTags()}
              {renderAllergenTags()}
              {renderIngredientExclusionTags()}
            </ContentContainer>
          ) : (
            <>
              <ContentContainer>
                {(isEditable && mealPlanAction === MealPlanAssignOption.NEW_MEAL_PLAN) && (
                <>
                  <ItemContainer>
                    <Label>{texts.filterBy}</Label>
                    <Select
                      value={selectedFilter}
                      onChange={handleFilterChange}
                      options={filterSelectOptions}
                      displayEmpty
                    />
                  </ItemContainer>
                  <ItemContainer>
                    <Label>{texts.selectPlan}</Label>
                    <MealPlanSelect
                      value={selectedMealPlanId}
                      onChange={handleMealPlanTemplateSelect}
                      mealPlanOptions={filteredMealPlans}
                    />
                  </ItemContainer>
                </>
                )}
                <ItemContainer>
                  <Label>{texts.name}</Label>
                  <StyledTextField
                    value={newMealPlanName}
                    placeholder={texts.namePlaceholder}
                    disabled={!isEditable}
                    onChange={(e) => setNewMealPlanName(e.target.value)}
                  />
                </ItemContainer>
                {(!!assignmentApprovalDate && mealPlanAction === MealPlanAssignOption.USE_PREVIOUS_MEAL_PLAN) && (
                <StyledDateContainer>
                  {format(texts.updateDate, {
                    date: moment(assignmentApprovalDate.toDate()).format(DateFormat.MONTH_NAME_DATE_FORMAT),
                  })}
                </StyledDateContainer>
                )}
                {isLoading && (
                <LoadingIndicatorContainer>
                  <RefreshIcon />
                  {texts.loadingMealPlan}
                </LoadingIndicatorContainer>
                )}
                {showRegenerateButton && (
                <PrimaryButton
                  onClick={handleRegeneratePlan}
                  variant="dark"
                  size="medium"
                  icon={<LightRefreshIcon />}
                >
                  {texts.regeneratePlan}
                </PrimaryButton>
                )}
              </ContentContainer>
              <ContentContainer>
                <ItemContainer>
                  <Label>{texts.note}</Label>
                  <StyledTextField
                    value={assignmentNote}
                    onChange={(e) => setAssignmentNote?.(e.target.value)}
                    placeholder={texts.notePlaceholder}
                    disabled={!isEditable}
                  />
                </ItemContainer>
                {!!newMacroAverages?.percentages?.protein && (
                <ItemContainer>
                  <Label>{texts.macros}</Label>
                  <ItemList>
                    <InfoTag>
                      {ReactHtmlParser(format(texts.protein, { percentage: newMacroAverages.percentages.protein }))}
                    </InfoTag>
                    <InfoTag>
                      {ReactHtmlParser(format(texts.carbs, { percentage: newMacroAverages.percentages.carbs }))}
                    </InfoTag>
                    <InfoTag>
                      {ReactHtmlParser(format(texts.fat, { percentage: newMacroAverages.percentages.fat }))}
                    </InfoTag>
                  </ItemList>
                </ItemContainer>
                )}
                {renderDietaryComplianceTags()}
                {renderAllergenTags()}
                {renderIngredientExclusionTags()}
              </ContentContainer>
            </>
          )}

          {(isEditable && (!!selectedMealPlanId || newMealTimes.length > 0)) && (
          <ContentContainer>
            <AddButton onClick={() => openRecipeModal()}>{texts.addRecipes}</AddButton>
            <EditButton onClick={() => setIsMealTimesModalOpen(true)}>{texts.updateMealTimes}</EditButton>
          </ContentContainer>
          )}
          <ContentContainer>
            <MealPlanView
              mealTimes={newMealTimes}
              scaleMeals={scaleMeals}
              totalDailyCalories={totalDailyCalories}
              macrosPercentages={macrosPercentages}
              allowEdit={isEditable}
              handleDelete={handleDeleteRecipe}
              handleSaveRecipe={onClickSaveRecipe}
              onAddRecipe={openRecipeModal}
              moveMeal={handleMoveRecipe}
              excludedIngredients={excludedIngredients}
            />
          </ContentContainer>
        </>
        )}
        <AddRecipeModal
          isOpen={isRecipeModalOpen}
          onAddRecipe={handleAddRecipe}
          mealTimes={newMealTimes}
          onClose={closeRecipeModal}
          selectedBucketId={selectedBucketId}
          excludedIngredients={excludedIngredients}
        />
        {isMealTimesModalOpen && (
        <EditMealTimesModal
          isOpen={isMealTimesModalOpen}
          onClose={() => setIsMealTimesModalOpen(false)}
          mealPlanMealTimes={newMealTimes}
          setMealPlanMealTimes={setNewMealTimes}
        />
        )}
      </Container>
      <AIMealPlanModal
        open={aiMealPlanModalOpen}
        setOpen={setAIMealPlanModalOpen}
        handleClose={handleCloseAIMealPlanModal}
      />
      <DialogRoundedModal
        open={isRecipeNameModalOpen}
        onClose={handleCloseRecipeNameModal}
        title={texts.saveRecipe}
        description={texts.saveRecipeDescription}
        actionButtons={(
          <>
            <CancelButton onClick={handleCloseRecipeNameModal}>
              {texts.cancel}
            </CancelButton>
            <SaveButton
              onClick={handleSaveRecipe}
              disabled={!recipeNameToUpdate.trim()}
            >
              {texts.save}
            </SaveButton>
          </>
        )}
      >
        <StyledDialogContent>
          <StyledText>{texts.recipeName}</StyledText>
          <StyledTextField
            value={recipeNameToUpdate}
            onChange={(e) => setRecipeNameToUpdate(e.target.value)}
            placeholder={texts.recipeNamePlaceholder}
            autoFocus
          />
        </StyledDialogContent>
      </DialogRoundedModal>
      <LoadingOverlay isLoading={isLoadingOverlayOpen} />
    </>
  );
};

UserMealPlan.propTypes = {
  isEditable: PropTypes.bool,
  totalDailyCalories: PropTypes.number,
  scaleMeals: PropTypes.bool,
  macrosPercentages: PropTypes.object,
  defaultAction: PropTypes.string,
  userId: PropTypes.string,
  onIsAIGeneratedChange: PropTypes.func.isRequired,
  feedbackHandler: PropTypes.bool.isRequired,
  setFeedbackHandler: PropTypes.func.isRequired,
};

UserMealPlan.defaultProps = {
  isEditable: true,
  totalDailyCalories: null,
  scaleMeals: false,
  userId: '',
  macrosPercentages: null,
  defaultAction: '',
};

export default observer(UserMealPlan);
